import React, { useEffect, useState } from "react";
import { db } from "../firebaseConfig";
import { collection, getDocs, doc, updateDoc, serverTimestamp, addDoc, getDoc, deleteDoc } from "firebase/firestore";
import Modal from "../components/modal";

const OnHoldInventoryView = ({ user }) => {
  const [items, setItems] = useState([]);
  const [pastItems, setPastItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("current"); // "current" or "past"
  const [brandFilter, setBrandFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  useEffect(() => {
    const fetchItems = async () => {
      const currentSnapshot = await getDocs(collection(db, "onHold"));
      const pastSnapshot = await getDocs(collection(db, "logs"));

      const currentItems = currentSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        brand: doc.data().brand || "Unknown",
        holdDate: doc.data().holdDate?.toDate().toLocaleString() || "No Date",
      }));

      const pastItems = pastSnapshot.docs
        .filter(doc => doc.data().action === "Removed from Hold")
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          brand: doc.data().brand || "Unknown",
          timestamp: doc.data().timestamp?.toDate().toLocaleString() || "No Date",
        }));

      setItems(currentItems);
      setPastItems(pastItems);
      setFilteredItems(currentItems);
    };

    fetchItems();
  }, []);

  const applyFilters = () => {
    const lowerCaseBrandFilter = brandFilter.toLowerCase();

    const filtered = (activeTab === "current" ? items : pastItems).filter(item => {
      const matchesBrand = item.brand?.toLowerCase().includes(lowerCaseBrandFilter);
      const matchesDate =
        !dateFilter ||
        (item.holdDate && new Date(item.holdDate).toLocaleDateString() === dateFilter) ||
        (item.timestamp && new Date(item.timestamp).toLocaleDateString() === dateFilter);
      return matchesBrand && matchesDate;
    });

    setFilteredItems(filtered);
  };

  const openCommentModal = async (item) => {
    setSelectedItem(item);

    let commentRef;
    if (activeTab === "current") {
      commentRef = collection(db, "onHold", item.id, "comments");
    } else {
      commentRef = collection(db, "logs", item.id, "comments");
    }

    const commentSnapshot = await getDocs(commentRef);
    const commentData = commentSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      message: doc.data().message || "No comments",
      timestamp: getFormattedDate(doc.data().timestamp),
    }));
    setComments(commentData);
    setShowModal(true);
  };

  const addComment = async () => {
    if (!newComment.trim()) return;

    const userDoc = await getDoc(doc(db, "users", user.uid));
    const fullName = `${userDoc.data()?.firstName || ""} ${userDoc.data()?.lastName || ""}`.trim();

    const newCommentData = {
      user: fullName || user.email,
      message: newComment,
      timestamp: serverTimestamp(),
    };

    const commentRef = collection(db, "onHold", selectedItem.id, "comments");
    await addDoc(commentRef, newCommentData);

    setComments(prev => [{ ...newCommentData, timestamp: new Date() }, ...prev]);
    setNewComment("");
  };

  const getFormattedDate = (timestamp) => {
    try {
      if (!timestamp) return "No Date";
      if (timestamp instanceof Date) return timestamp.toLocaleString();
      if (timestamp?.toDate) return timestamp.toDate().toLocaleString();
      if (timestamp?.seconds) return new Date(timestamp.seconds * 1000).toLocaleString();
      if (typeof timestamp === "string") return new Date(timestamp).toLocaleString();
      return "Invalid Date";
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  const removeFromHold = async (item) => {
    try {
      console.log("🔄 Starting removal for:", item);

      // Delete the onHold entry
      const onHoldRef = doc(db, "onHold", item.id);
      await deleteDoc(onHoldRef);
      console.log(`✅ Successfully deleted onHold entry: ${item.id}`);

      // Update the original inventory item if needed
      if (item.originalItemId) {
        const inventoryRef = doc(db, "inventory", item.originalItemId);
        const inventoryDoc = await getDoc(inventoryRef);

        if (inventoryDoc.exists()) {
          const currentItem = inventoryDoc.data();
          const isBackdoor = item.originalItemId.endsWith("__backdoor");
          const updatedFields = isBackdoor
            ? { backdoor: (currentItem.backdoor ?? 0) + 1 }
            : { visible: (currentItem.visible ?? 0) + 1 };

          console.log(`🔄 Updating original item ${item.originalItemId} with:`, updatedFields);

          await updateDoc(inventoryRef, {
            ...updatedFields,
            lastUpdated: serverTimestamp(),
          });

          console.log(`✅ Updated original inventory item: ${item.originalItemId}`);
        } else {
          console.warn(`⚠️ Original inventory item not found: ${item.originalItemId}`);
        }
      }

      // Log the removal action
      await addDoc(collection(db, "onHold"), {
        originalItemId: selectedItem.id,
        originalSection: section,
        brand: brand,
        model: selectedItem.model?.trim() || "Unknown",
        reason: selectedItem.reason?.trim() || "No reason provided",
        holdBy: firstName || user.email,
        holdDate: serverTimestamp(), // Ensure it is saved correctly
        lastUpdated: serverTimestamp(),
      });

      console.log(`📜 Logged removal action for item: ${item.id}`);

      // Remove the item from the local state
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      setFilteredItems((prev) => prev.filter((i) => i.id !== item.id));

      console.log(`✅ Successfully removed item from hold: ${item.id}`);
    } catch (error) {
      console.error("❌ Error removing item from hold:", error);
    }
  };

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <h2 className="text-xl font-bold mb-4">On Hold Inventory</h2>

      {/* Tabs */}
      <div className="flex mb-4">
        <button
          className={`flex-1 p-2 ${activeTab === "current" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
          onClick={() => {
            setActiveTab("current");
            setFilteredItems(items);
          }}
        >
          Current Holds
        </button>
        <button
          className={`flex-1 p-2 ${activeTab === "past" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
          onClick={() => {
            setActiveTab("past");
            setFilteredItems(pastItems);
          }}
        >
          Past Holds
        </button>
      </div>

      {/* Filter Inputs */}
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          placeholder="Filter by brand"
          value={brandFilter}
          onChange={(e) => {
            setBrandFilter(e.target.value);
            applyFilters();
          }}
          className="flex-grow border p-2"
        />
        <input
          type="date"
          value={dateFilter}
          onChange={(e) => {
            setDateFilter(e.target.value);
            applyFilters();
          }}
          className="border p-2"
        />
      </div>

      {/* On Hold Items Table */}
      <table className="w-full border text-sm">
        <thead className="bg-gray-200">
          <tr>
            <th className="p-2">Brand</th>
            <th className="p-2">Model</th>
            <th className="p-2">Hold By</th>
            <th className="p-2">Hold Date</th>
            <th className="p-2">Comments</th>
          </tr>
        </thead>
        <tbody>
          {filteredItems.map(item => (
            <tr key={item.id} className="border-t even:bg-gray-50">
              <td className="p-2">{item.brand || "Unknown"}</td>
              <td className="p-2 text-center">{item.model || "Unknown"}</td>
              <td className="p-2 text-center">{item.holdBy || "Unknown"}</td>
              <td className="p-2 text-center">{getFormattedDate(item.holdDate || item.timestamp)}</td>
              <td className="p-2 text-center">
                <button
                  className="text-blue-600 underline"
                  onClick={() => openCommentModal(item)}
                >
                  View Comments
                </button>
              </td>
              {activeTab === "current" && (
                <td className="p-2 text-center">
                  <button
                    className="bg-red-500 text-white py-1 px-3 rounded hover:bg-red-600 transition"
                    onClick={() => removeFromHold(item)}
                  >
                    Remove from Hold
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Comment Modal */}
      {showModal && selectedItem && (
        <Modal onClose={() => setShowModal(false)}>
          <h2 className="text-xl font-bold mb-4">
            Comments for {selectedItem?.brand || "Unknown"}
          </h2>

          <ul className="mb-4 max-h-64 overflow-y-auto bg-gray-50 p-4 rounded-lg border border-gray-200">
            {comments.map((comment) => (
              <li key={comment.id} className="mb-4 bg-white p-4 rounded-lg border border-gray-300">
                <strong className="text-gray-700">{comment.user || "Unknown"}</strong>
                <div className="mt-2 text-gray-500 text-xs">
                  {getFormattedDate(comment.timestamp)}
                </div>
                <p>{comment.message}</p>
              </li>
            ))}
          </ul>
        </Modal>
      )}
    </div>
  );
};

export default OnHoldInventoryView;
